import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Book3D from "@/components/Book3D";
import ProductCard, { ProductActions, PriceLine, NotifyForm } from "@/components/ProductCard";
import { useSite } from "@/lib/SiteContext";
import { api, track } from "@/lib/api";

function useProducts(query = "") {
  const [products, setProducts] = useState(null);
  useEffect(() => {
    api.get(`/public/products${query}`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [query]);
  return products;
}

export default function Bookstore() {
  const { settings, volume } = useSite();
  const products = useProducts("?type=book");

  return (
    <div className="bg-ink min-h-screen" data-testid="bookstore-page">
      <Seo title="The Official Bookstore — Dirty Church Chronicles"
        description="The official bookstore of Dirty Church Chronicles: Volume I by Jermaine Miller. Kindle edition available now on Amazon; paperback, hardcover, and signed editions in preparation."
        path="/bookstore" />
      <section className="pt-40 pb-20 max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">The Official Bookstore</p>
          <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-6xl tracking-tight mb-4" data-testid="bookstore-title">
            Own the Chronicle
          </h1>
          <p className="font-reading text-xl text-paper/70 max-w-2xl">
            Every edition of Dirty Church Chronicles, direct from the official store. Kindle is available now on
            Amazon; print and signed editions are in preparation.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(products || []).map((p) => (
            <Reveal key={p.product_id}>
              <div className="border border-paper/10 bg-coal p-8 sm:p-10 flex flex-col sm:flex-row gap-8 items-start transition-colors duration-500 hover:border-paper/25"
                data-testid={`bookstore-product-${p.product_id}`}>
                <div className="w-28 shrink-0 aspect-[2/3] overflow-hidden border border-paper/15">
                  {settings.cover_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${settings.cover_url}`} alt={`Cover of ${p.title}`} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-b from-[#161311] to-[#0a0a0a]" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="font-display text-2xl text-paper">{p.title}</h2>
                    {p.badge && (
                      <span className={`font-ui text-[9px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border ${
                        p.availability === "buy_external" ? "border-gold/50 text-gold" : "border-paper/20 text-paper/45"}`}>
                        {p.badge}
                      </span>
                    )}
                  </div>
                  <p className="font-ui text-sm text-crimsonhi mt-1">{p.subtitle}</p>
                  <p className="font-reading text-lg text-paper/70 leading-relaxed mt-4">{p.description}</p>
                  <div className="mt-5 flex items-center gap-5 flex-wrap">
                    <PriceLine product={p} />
                    <ProductActions product={p} />
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-16 border border-paper/10 p-10 text-center" data-testid="bookstore-volume-one-cta">
            <p className="font-reading text-xl text-paper/70 mb-6">
              Explore Volume I in depth — artwork, editions, and specifications.
            </p>
            <Link to="/bookstore/volume-one" data-testid="bookstore-volume-one-link"
              className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] font-semibold bg-paper text-ink px-8 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-white hover:scale-[1.02]">
              Volume I — Full Details <ArrowRight size={14} />
            </Link>
            <p className="font-ui text-xs text-paper/40 mt-6">
              Volume II and future publications will appear here as they are confirmed. Kindle remains available
              through Amazon KDP as a separate retail channel.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

export function VolumeOne() {
  const { settings, volume } = useSite();
  const products = useProducts("?collection=volume-one");
  const kindle = (products || []).find((p) => p.product_id === "bk-kindle-v1");

  return (
    <div className="bg-ink min-h-screen" data-testid="volume-one-page">
      <Seo title={`${volume.title || "Dirty Church Chronicles: Volume I"} — Official Bookstore`}
        description={settings.seo_description} path="/bookstore/volume-one"
        jsonLd={{
          "@context": "https://schema.org", "@type": "Book",
          name: volume.title || "Dirty Church Chronicles: Volume I",
          author: { "@type": "Person", name: settings.author_name || "Jermaine Miller" },
          bookFormat: "https://schema.org/EBook", inLanguage: "en",
          url: "https://www.amazon.com/dp/B0HLV2X8VB",
        }} />
      <section className="relative pt-36 pb-24 overflow-hidden">
        <div className="absolute inset-0 book-spotlight" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-5 flex justify-center">
            <Book3D cover={settings.cover_url} spine={settings.spine_url} back={settings.back_url}
              title={volume.title} author={settings.author_name} />
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow mb-4">Official Bookstore · Volume One</p>
              <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-5xl leading-[0.98] tracking-tight" data-testid="volume-one-title">
                {volume.title || "Dirty Church Chronicles: Volume I"}
              </h1>
              <p className="font-reading italic text-xl text-gold/90 mt-3">by {settings.author_name || "Jermaine Miller"}</p>
              <p className="font-reading text-xl text-paper/80 leading-relaxed mt-7 max-w-2xl">{volume.synopsis}</p>
            </Reveal>
            {kindle && (
              <Reveal delay={0.15} className="mt-9 flex items-center gap-5 flex-wrap">
                <PriceLine product={kindle} />
                <span onClick={() => track("purchase_click", "volume-one-page")}><ProductActions product={kindle} /></span>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section className="paper-section py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <p className="eyebrow eyebrow-light mb-4">Editions</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-ink mb-12">Choose Your Edition</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(products || []).map((p) => (
              <Reveal key={p.product_id}>
                <div className="bg-ink p-8 sm:p-10 border border-ink/10" data-testid={`edition-${p.product_id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-2xl text-paper">{p.subtitle}</h3>
                    {p.badge && (
                      <span className={`font-ui text-[9px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border ${
                        p.availability === "buy_external" ? "border-gold/50 text-gold" : "border-paper/20 text-paper/45"}`}>
                        {p.badge}
                      </span>
                    )}
                  </div>
                  <p className="font-reading text-lg text-paper/70 leading-relaxed mt-4">{p.description}</p>
                  {p.specs && (
                    <ul className="mt-5 space-y-2">
                      {p.specs.split("\n").filter(Boolean).map((s, i) => (
                        <li key={i} className="font-ui text-sm text-paper/55 flex gap-3">
                          <span className="text-crimsonhi">✦</span>{s}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-6 flex items-center gap-5 flex-wrap">
                    <PriceLine product={p} />
                    <ProductActions product={p} />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14 border-l-2 border-crimson pl-8 max-w-3xl" data-testid="shipping-info">
              <p className="font-display text-xl text-ink mb-3">Shipping & Fulfillment</p>
              <p className="font-reading text-lg text-ink/75 leading-relaxed">
                Kindle is delivered instantly by Amazon. Paperback and hardcover editions will print on demand and
                ship directly from our printing partner when the store launches. Signed editions ship separately
                from author-managed inventory. Books and merchandise always ship independently — each shipment is
                confirmed with its own tracking where available.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
